import React from 'react';
import { useTranslation } from 'react-i18next';
import { Pagination } from 'antd';

export interface Transaction {
  id: string;
  code: string;
  time: string;
  type: 'refund' | 'investment' | 'profit' | 'commission';
  amount: number;
  status: 'completed' | 'pending' | 'failed';
}

interface TransactionsTabProps {
  transactions?: Transaction[];
  stats?: {
    totalIn: number;
    totalOut: number;
    totalTransactions: number;
  };
  currentPage?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
}

const TransactionsTab: React.FC<TransactionsTabProps> = ({
  transactions,
  stats: propStats,
  currentPage = 1,
  totalItems = 0,
  onPageChange
}) => {
  const { t } = useTranslation();

  const data = transactions || [];

  // Use provided stats or fallback to calculation (e.g. if not paginated yet fully supported)
  // But if paginated, calculation on 'data' is wrong for total stats.
  // We prefer propStats.
  const stats = propStats || {
    totalIn: data.filter(t => t.type === 'investment').reduce((sum, t) => sum + t.amount, 0),
    totalOut: data.filter(t => t.type === 'refund' || t.type === 'profit').reduce((sum, t) => sum + t.amount, 0),
    totalTransactions: totalItems || data.length,
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('vi-VN', { maximumFractionDigits: 0 }) + ' đ';
  };

  const getTypeLabel = (type: Transaction['type']) => {
    switch (type) {
      case 'investment':
        return t('investorDetail.transactions.investment', 'Đầu tư');
      case 'refund':
        return t('investorDetail.transactions.refund', 'Hoàn tiền');
      case 'profit':
        return t('investorDetail.transactions.profit', 'Nhận lãi');
      case 'commission':
        return t('investorDetail.transactions.commission', 'Hoa hồng');

      default:
        return type;
    }
  };

  const getStatusBadge = (status: Transaction['status']) => {
    switch (status) {
      case 'completed':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
            {t('investorDetail.transactions.completed', 'Hoàn thành')}
          </span>
        );
      case 'pending':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
            {t('investorDetail.transactions.pending', 'Đang xử lý')}
          </span>
        );
      case 'failed':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
            {t('investorDetail.transactions.failed', 'Thất bại')}
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Title */}
      <h3 className="text-base font-semibold text-gray-900 dark:text-white">
        {t('investorDetail.transactions.overview', 'Tổng quan giao dịch')}
      </h3>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
            {t('investorDetail.transactions.totalIn', 'Tổng tiền vào')}
          </p>
          <p className="text-lg font-semibold text-green-600 dark:text-green-400">
            {formatCurrency(stats.totalIn)}
          </p>
        </div>

        <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
            {t('investorDetail.transactions.totalOut', 'Tổng tiền ra')}
          </p>
          <p className="text-lg font-semibold text-red-600 dark:text-red-400">
            {formatCurrency(stats.totalOut)}
          </p>
        </div>

        <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
            {t('investorDetail.transactions.totalTransactions', 'Tổng giao dịch')}
          </p>
          <p className="text-lg font-semibold text-gray-900 dark:text-white">
            {stats.totalTransactions}
          </p>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[500px]">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {t('investorDetail.transactions.code', 'Mã GD')}
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {t('investorDetail.transactions.time', 'Thời gian')}
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {t('investorDetail.transactions.type', 'Loại GD')}
              </th>
              <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {t('investorDetail.transactions.amount', 'Số tiền')}
              </th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {t('investorDetail.transactions.status', 'Trạng thái')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {data.map((tx) => (
              <tr key={tx.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-3 py-3 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
                  {tx.code}
                </td>
                <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                  {tx.time}
                </td>
                <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                  {getTypeLabel(tx.type)}
                </td>
                <td className="px-3 py-3 text-sm text-right font-medium text-gray-900 dark:text-white whitespace-nowrap">
                  {formatCurrency(tx.amount)}
                </td>
                <td className="px-3 py-3 text-center whitespace-nowrap">
                  {getStatusBadge(tx.status)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalItems > 0 && onPageChange && (
        <div className="flex justify-end pt-4">
          <Pagination
            current={currentPage}
            total={totalItems}
            pageSize={10}
            onChange={onPageChange}
            showSizeChanger={false}
          />
        </div>
      )}
    </div>
  );
};

export default TransactionsTab;
